/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-magic-numbers

import { ActivatedRouteSnapshot } from '@angular/router';
import { DeepLinkFactory, getRouteQueryParam, getUrlSegmentPath, hasUrlSegmentPath } from '@gh/common-shared';
import { HpeInitType } from '@gh/builder-shared-data';

const getDeepLinkName = (route: ActivatedRouteSnapshot) => getUrlSegmentPath(route, 0);

const DEEP_LINK_CONFIG: Hash<DeepLinkFactory> = {
  'todo': (route: ActivatedRouteSnapshot) => ({
    commands: [`/todo`],
    extras: {
      queryParams: {
        id: getUrlSegmentPath(route, 1),
      },
    },
  }),
  'contact': (route: ActivatedRouteSnapshot) => ({
    commands: [`/contacts/${getUrlSegmentPath(route, 1)}`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_menu_contacts',
        parentRoute: '/contacts',
      },
    },
  }),
  'color-selection': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 2)) {
      if (getUrlSegmentPath(route, 1) === 'job') {
        return {
          commands: [`/job-admin/jobs/${getUrlSegmentPath(route, 2)}/color-selection`],
          extras: {
            queryParams: {
              jobId: getUrlSegmentPath(route, 2),
              show: route.queryParams.show,
              parentTitle: 'lbl_color_selections',
              parentRoute: `/job-admin/job-color-selection`,
            },
          },
        };
      }
    }
  },
  'estimating': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 2)) {
      if (getUrlSegmentPath(route, 1) === 'job') {
        return {
          commands: [`/job-admin/jobs/${getUrlSegmentPath(route, 2)}/estimate`],
          extras: {
            queryParams: {
              show: route.queryParams.show,
              parentTitle: 'lbl_job_estimates',
              parentRoute: `/estimating/job-estimates`,
            },
          },
        };
      }
    }
  },
  'specs': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 1)) {
      if (getUrlSegmentPath(route, 1) === 'job') {
        return {
          commands: [`/job-admin/jobs/${getUrlSegmentPath(route, 2)}/spec`],
          extras: {
            queryParams: {
              forDpp: false,
              show: route.queryParams.show,
              parentTitle: 'lbl_job_specifications',
              parentRoute: `/job-admin/job-specifications`,
            },
          },
        };
      }
    }
  },
  'jobs': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 2)) {
      return {
        commands: [`/job-admin/jobs/${getUrlSegmentPath(route, 1)}/change-orders/${getUrlSegmentPath(route, 3)}`],
        extras: {
          queryParams: {
            show: route.queryParams.show,
            parentTitle: 'lbl_change_orders',
            parentRoute: `/job-admin/job-change-orders/${getUrlSegmentPath(route, 1)}`,
          },
        },
      };
    }
  },
  'job': (route: ActivatedRouteSnapshot) => {
    if (getUrlSegmentPath(route, 1).toLowerCase() === 'dpp') {
      return {
        commands: [`/job-admin/jobs/dpp/${getUrlSegmentPath(route, 2)}`],
        extras: {
          queryParams: {
            show: route.queryParams.show,
            parentTitle: 'lbl_jobs',
            parentRoute: '/job-admin/jobs',
          },
        },
      };
    }

    return {
      commands: [`/job-admin/jobs/${getUrlSegmentPath(route, 1)}`],
      extras: {
        queryParams: {
          show: route.queryParams.show,
          parentTitle: 'lbl_jobs',
          parentRoute: '/job-admin/jobs',
        },
      },
    };
  },
  'hlp': (route: ActivatedRouteSnapshot) => ({
    commands: [`/sales/hlp/${getUrlSegmentPath(route, 1)}`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_hlps',
        parentRoute: '/sales/hlp',
      },
    },
  }),
  'house-plans': (route: ActivatedRouteSnapshot) => ({
    commands: [`/estimating/house-plans/${getUrlSegmentPath(route, 1)}/estimate`],
    extras: {
      queryParams: {
        initType: HpeInitType.Open,
        show: route.queryParams.show,
        parentTitle: 'lbl_house_plans',
        parentRoute: '/estimating/house-plans',
      },
    },
  }),
  'price-books': (route: ActivatedRouteSnapshot) => ({
    commands: [`/estimating/price-books/${getUrlSegmentPath(route, 1)}`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_price_books',
        parentRoute: '/estimating/price-books',
      },
    },
  }),
  'construction-schedule': (route: ActivatedRouteSnapshot) => ({
    commands: [`/schedules/construction/${getUrlSegmentPath(route, 1)}/gantt-chart`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_construction_schedules',
        parentRoute: '/schedules/construction',
      },
    },
  }),
  'pre-construction-schedule': (route: ActivatedRouteSnapshot) => ({
    commands: [`/schedules/pre-construction/${getUrlSegmentPath(route, 1)}/gantt-chart`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_pre_construction_schedules',
        parentRoute: '/schedules/pre-construction',
      },
    },
  }),
  'maintenance-schedule': (route: ActivatedRouteSnapshot) => ({
    commands: [`/schedules/maintenance/${getUrlSegmentPath(route, 1)}/gantt-chart`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_maintenance_schedules',
        parentRoute: '/schedules/maintenance',
      },
    },
  }),
  'sales-schedule': (route: ActivatedRouteSnapshot) => ({
    commands: [`/schedules/sales/${getUrlSegmentPath(route, 1)}/gantt-chart`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_sales_schedules',
        parentRoute: '/schedules/sales',
      },
    },
  }),
  'draws': (route: ActivatedRouteSnapshot) => ({
    commands: [`/accounting/draws/${getUrlSegmentPath(route, 1)}/draw-details`],
    extras: {
      queryParams: {
        show: route.queryParams.show,
        parentTitle: 'lbl_draws',
        parentRoute: '/accounting/draws',
      },
    },
  }),
  'email-templates': (route: ActivatedRouteSnapshot) => ({
    'job': {
      commands: [`/setup/email-templates/templates/${getUrlSegmentPath(route, 2)}`],
      extras: {
        queryParams: {
          parentRoute: '/setup/email-templates/templates_job',
        },
      },
    },
    'system': {
      commands: [`/setup/email-templates/templates/${getUrlSegmentPath(route, 2)}`],
      extras: {
        queryParams: {
          parentRoute: '/setup/email-templates/templates_system',
        },
      },
    },
    'internal': {
      commands: [`/setup/email-templates/templates/${getUrlSegmentPath(route, 2)}`],
      extras: {
        queryParams: {
          parentRoute: '/setup/email-templates/templates_internal',
        },
      },
    },
    'layout': {
      commands: [`/setup/email-templates/templates/${getUrlSegmentPath(route, 2)}`],
      extras: {
        queryParams: {
          parentRoute: '/setup/email-templates/templates_layout',
        },
      },
    },
  })[getUrlSegmentPath(route, 1)],
  'leads': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 2)) {
      const leadSubPath = getUrlSegmentPath(route, 2);
      if (leadSubPath === 'sales-folders') {
        return {
          commands: [`/sales/leads/${getUrlSegmentPath(route, 1)}`],
          extras: {
            queryParams: {
              salesFolderId: getUrlSegmentPath(route, 3),
              parentTitle: 'lbl_leads',
              parentRoute: '/sales/leads',
            },
          },
        };
      }
    } else if (getUrlSegmentPath(route, 1) === 'board') {
      return {
        commands: [`/sales/leads-board`],
      };
    } else {
      return {
        commands: [`/sales/leads/${getUrlSegmentPath(route, 1)}`],
        extras: {
          queryParams: {
            parentTitle: 'lbl_leads',
            parentRoute: '/sales/leads',
          },
        },
      };
    }
  },
  // quotations
  'sales-folders': (route: ActivatedRouteSnapshot) => {
    if (hasUrlSegmentPath(route, 2)) {
      const leadSubPath = getUrlSegmentPath(route, 2);
      if (leadSubPath === 'quotations') {
        return {
          commands: [`/sales/sales-folders/${getUrlSegmentPath(route, 1)}/rfq/quotations/${getUrlSegmentPath(route, 3)}`],
          extras: {
            queryParams: {
              parentTitle: 'lbl_rfq_management',
              parentRoute: '/sales/rfq-management',
            },
          },
        };
      }
    }
  },
  'xero': (route: ActivatedRouteSnapshot) => ({
    commands: [`/setup/system-variables/accounts/xero`],
    extras: {
      queryParams: {
        code: getRouteQueryParam(route, 'code'),
      },
    },
  }),
};

export const BUILDER_PORTAL_DEEP_LINKS: DeepLinkFactory = (route: ActivatedRouteSnapshot) => {
  const childFactory = DEEP_LINK_CONFIG[getDeepLinkName(route)];
  return childFactory ? childFactory(route) : void 0;
};
